import { styled } from "styled-components";
import HelpCenterHeader from "../components/HelpCenterHeader";
import { Button, Form, Input, Select, Upload } from "antd";
import TextArea from "antd/es/input/TextArea";
import {
  BASE_URL,
  FORMAT,
  contactSupport,
  openNotification,
  scrollBarStyles,
  uploadFile,
  useDocumentTitle,
} from "@dataone/lib";
import { useMutation } from "@tanstack/react-query";
import { RcFile } from "antd/es/upload";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { LinkIcon } from "@dataone/icons";

interface HelpCenterContactPageProps {
  children?: React.ReactNode;
}

const HelpCenterContactPage = (props: HelpCenterContactPageProps) => {
  const { Option } = Select;
  const navigate = useNavigate();
  const { mutate: upload } = useMutation(uploadFile);
  const { mutate: sendMessage, isLoading } = useMutation(contactSupport);
  const [contactForm] = Form.useForm();
  const fileListValue = Form.useWatch("files", contactForm)?.fileList;
  const [uploadError, setUploadError] = useState("");
  useDocumentTitle("OneGate | Help Center");
  const beforeFileUpload = async (file: RcFile) => {
    const isImage = FORMAT.image.types.includes(file.type);
    const isLarge = file.size > FORMAT.MAX_IMAGE_SIZE;

    if (!isImage) {
      setUploadError("Please upload the right format file!");
      return false;
    }
    if (isLarge) {
      setUploadError("File must be less than 2 MB.");
      return false;
    }
    setUploadError("");
    return true;
  };

  const handleContactSupport = () => {
    sendMessage(
      {
        title: contactForm.getFieldValue("title"),
        category: contactForm.getFieldValue("category"),
        description: contactForm.getFieldValue("description"),
        files: fileListValue?.map((file: any) => file.response),
      },
      {
        onSuccess: ({ message }) => {
          contactForm.resetFields();
          openNotification(message, "success");
          navigate("/support");
        },
      }
    );
  };
  const rules = [
    {
      whitespace: true,
      required: true,
      message: "Please provide a value for this field.",
    },
  ];

  return (
    <StyledHelpCenterContactPage>
      <HelpCenterHeader secondTitle="Contact us" />
      <Title>Contact OneGate Support</Title>
      <StyledDescription>
        Due to high support volume, it may take longer than usual to hear back
        from our Support Agents.
      </StyledDescription>
      <StyledForm>
        <Form form={contactForm} onFinish={handleContactSupport}>
          <InlinedStyle>
            <Form.Item name="title" className="form-item" rules={rules}>
              <Input className="input" maxLength={255} placeholder="Title" />
            </Form.Item>
            <Form.Item name="category" className="form-item" rules={rules}>
              <StyledSelect bordered={false} placeholder="Issue Type">
                <Option value="Account Access">Account Access</Option>
                <Option value="Payment Issue">Payment Issue</Option>
                <Option value="Data Protection Concern">
                  Data Protection Concern
                </Option>
                <Option value="Other">Other</Option>
              </StyledSelect>
            </Form.Item>
          </InlinedStyle>
          <Form.Item name="description" rules={rules}>
            <TextArea className="input-textarea" placeholder="Comment" />
          </Form.Item>
          <Form.Item
            name="files"
            rules={[
              {
                validator: async () => {
                  if (uploadError)
                    return Promise.reject(new Error(uploadError));
                  return true;
                },
              },
            ]}
          >
            <Upload
              className="upload_input"
              listType="picture"
              accept=".jpg,.jpeg,.webp,.png"
              beforeUpload={beforeFileUpload}
              multiple
              maxCount={3}
              customRequest={async ({ onSuccess, onError, file }) => {
                upload(file as File, {
                  onSuccess: (message) => {
                    onSuccess?.(`${BASE_URL}/storage/public/${message}`);
                    setUploadError("");
                  },
                  onError: () => {
                    onError?.({} as any);
                    setUploadError(
                      "An error occurred while uploading the file"
                    );
                  },
                  onSettled: () => {
                    setTimeout(() => {
                      contactForm.validateFields(["files"]);
                    }, 0);
                  },
                });
              }}
            >
              <Button className="upload_button" icon={<LinkIcon />}>
                Add an attachment
              </Button>
            </Upload>
          </Form.Item>
          <Button
            disabled={isLoading}
            className="send-button"
            htmlType="submit"
          >
            Send Request
          </Button>
        </Form>
      </StyledForm>
    </StyledHelpCenterContactPage>
  );
};

export default HelpCenterContactPage;

const StyledHelpCenterContactPage = styled.div`
  padding: 5px 10px;
`;
const Title = styled.h1`
  padding: 15px 46px;
  color: #4974f6;
  padding-bottom: 0;
`;
const StyledDescription = styled.p`
  padding-left: 48px;
  width: 570px;
  font-weight: 700;
`;
const InlinedStyle = styled.div`
  display: flex;
  gap: 10px;
  .ant-select-selector {
    height: 40px !important;
    display: flex !important;
    align-items: center !important;
  }
  .ant-select.ant-select-in-form-item {
    border-radius: 6px;
    height: 100%;
    background: #eff1fd;
    border: none;
  }
`;
const StyledSelect = styled(Select)`
  width: 100px;
  &:hover {
    border: none;
  }
`;
const StyledForm = styled.div`
  width: 100%;
  padding: 30px 20%;
  overflow: auto;
  height: calc(100vh - 320px);
  ${scrollBarStyles}
  .ant-input {
    border-radius: 6px;
    background: #eff1fd !important;
    height: 40px;
    border: none !important;
    box-shadow: 0px 4px 4px 0px #fff !important;
  }
  .ant-form-item .ant-form-item-explain-error {
    font-size: 12px;
  }
  .input-textarea {
    height: 80px;
  }
  .form-item {
    flex: 1;
  }
  .ant-btn-default {
    border-radius: 10px;
    background: #4974f6;
    border: none;
    width: 65%;
    color: #fff;
    height: 39px;
    &:hover {
      border-radius: 10px;
      background: #4974f6;
      border: none;
      color: #fff !important;
    }
  }
  .react-tel-input .form-control {
    height: 50px !important;
    width: 100%;
    border-radius: 10px;
    background: #fff;
    height: 50px;
    border: none;
    padding-left: 80px;
  }
  .react-tel-input .flag-dropdown {
    border-radius: 11px 0 0 11px !important;
    background-color: #fff !important;
    border: none !important;
    margin: 10px;
  }
  .react-tel-input .selected-flag {
    width: 60px;
    border-right: 1px solid #828d9c !important;
  }
  .react-tel-input .selected-flag .arrow {
    left: 30px !important;
  }
  .upload_button {
    width: 100%;
    background: #eff1fd;
    color: #4974f6;
    border-radius: 10px;
    display: flex;
    align-items: center;
    border: none;
    &:hover {
      background: #eff1fd;
      border: none;
      color: #4974f6 !important;
      opacity: 0.6;
    }
    svg {
      font-size: 20px;
    }
  }
  .send-button {
    width: 100%;
    background: #4974f6;
    color: #fff;
    border-radius: 10px;
    border: none;
    height: 40px;
    margin: 8px 0;
    &:hover {
      background: #4974f6;
      border: none;
      opacity: 0.8;
    }
  }
`;
